import type { FC, ReactNode } from 'react';
import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import {
	AutoConvertActionSubject,
	AutoConvertEvent,
	sendEditorConversionAnalyticsPageEvent,
} from '../analytics.ts';
import { useAutoConvertActions } from '../AutoConversion/useAutoConvertState.tsx';
import { checkRevertFlag, RevertEditorSuccess } from '../EditorRevert/index.ts';

export type AutoConversionContextValue = {
	editorProperty: string;
	fabricEditorSupported: boolean | null | undefined;
	isAutoConverted: boolean | null | undefined;
	queryDataId: string | null | undefined;
};

export const AutoConversionByLineContext: React.Context<AutoConversionContextValue> =
	createContext<AutoConversionContextValue>({
		editorProperty: '',
		fabricEditorSupported: false,
		isAutoConverted: false,
		queryDataId: '',
	});

type AutoConversionContextProviderProps = {
	editorProperty: string;
	fabricEditorSupported: boolean | null | undefined;
	isAutoConverted: boolean;
	queryDataId: string | null | undefined;
	children?: ReactNode;
};

export const AutoConversionByLineContextProvider: FC<AutoConversionContextProviderProps> = ({
	editorProperty,
	fabricEditorSupported,
	isAutoConverted,
	queryDataId,
	children,
}) => {
	const isFabric = editorProperty === 'v2' || isAutoConverted;
	const autoConvertActions = useAutoConvertActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (queryDataId && expValEquals('confluence_remove_editor_type_byline', 'isEnabled', true)) {
			autoConvertActions.setIsAutoConverted(!!isAutoConverted);
		}
	}, [queryDataId, isAutoConverted, autoConvertActions]);

	useEffect(() => {
		if (
			queryDataId &&
			isAutoConverted &&
			expValEquals('confluence_remove_editor_type_byline', 'isEnabled', true)
		) {
			sendEditorConversionAnalyticsPageEvent(
				createAnalyticsEvent,
				{
					pageAction: AutoConvertEvent.VIEWED,
					source: 'ViewPageAutoConversion',
					setting: {
						// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate moved from AutoConversionByLineComponent to keep the VIEWED payload unchanged.
						isAllPagesEnabled: fg('confluence_frontend_fabric_editor_all_pages'),
					},
				},
				AutoConvertActionSubject.AUTO_CONVERSION,
			);
		}
	}, [queryDataId, createAnalyticsEvent, isAutoConverted]);

	const autoConversionContextValue = useMemo(
		() => ({
			editorProperty,
			fabricEditorSupported,
			isAutoConverted,
			queryDataId,
		}),
		[editorProperty, fabricEditorSupported, isAutoConverted, queryDataId],
	);

	return (
		<AutoConversionByLineContext.Provider value={autoConversionContextValue}>
			{children}
			{!isFabric && queryDataId && checkRevertFlag(queryDataId) && (
				<div data-testid="auto-editor-conversion-revert-success">
					<RevertEditorSuccess />
				</div>
			)}
		</AutoConversionByLineContext.Provider>
	);
};

export const useAutoConversionByLineContext = (): AutoConversionContextValue =>
	useContext(AutoConversionByLineContext);
